/**
 * Helper functions for FormPreview
 */

/**
 * Evaluate a comparison operator against a value
 */
const evaluateOperator = (operator, sourceValue, expected) => {
  switch (operator) {
    case '=':
    case 'equals':
      return sourceValue === expected || sourceValue === true;
    case '!=':
    case 'notEquals':
      return sourceValue !== expected && sourceValue !== true;
    case 'exists':
      return sourceValue !== undefined && sourceValue !== null && sourceValue !== '' && sourceValue !== false;
    case '!exists':
    case 'notExists':
      return !sourceValue || sourceValue === '' || sourceValue === false;
    case '>':
    case 'greaterThan':
      return Number(sourceValue) > Number(expected);
    case '<':
    case 'lessThan':
      return Number(sourceValue) < Number(expected);
    case '>=':
    case 'greaterOrEquals':
      return Number(sourceValue) >= Number(expected);
    case '<=':
    case 'lessOrEquals':
      return Number(sourceValue) <= Number(expected);
    default:
      return false;
  }
};

/**
 * Evaluate a single condition against form values.
 * Supports sourceType: 'question' (default), 'answer', 'section'.
 */
const evaluateCondition = (condition, formValues, schema, _visited = new Set()) => {
  const sourceType = condition.sourceType || 'question';

  switch (sourceType) {
    case 'question': {
      const sourceValue = formValues[condition.question];
      return evaluateOperator(condition.operator, sourceValue, condition.answerCoding || condition.answer);
    }

    case 'answer': {
      // Check if a specific answer is selected in a question
      const qValue = formValues[condition.question];
      const answerRef = condition.answerRef;
      if (!answerRef) return true;

      let isSelected;
      if (typeof qValue === 'object' && qValue !== null) {
        // Checkbox-style: { answerId: true/false }
        isSelected = !!qValue[answerRef];
      } else {
        // Radio/select-style: string value
        isSelected = qValue === answerRef;
      }

      switch (condition.operator) {
        case '=':
        case 'exists':
          return isSelected;
        case '!=':
        case '!exists':
          return !isSelected;
        default:
          return isSelected;
      }
    }

    case 'section': {
      // Check if a referenced section is currently visible
      const sectionRef = condition.sectionRef;
      if (!sectionRef) return true;

      // Prevent circular dependency evaluation
      if (_visited.has(sectionRef)) return true;
      _visited.add(sectionRef);

      const [pi, si] = sectionRef.split(':').map(Number);
      const targetSection = schema?.pages?.[pi]?.sections?.[si];
      if (!targetSection) return false;

      const isVisible = isSectionEnabled(targetSection, schema, formValues, _visited);
      switch (condition.operator) {
        case '=':
        case 'exists':
          return isVisible;
        case '!=':
        case '!exists':
          return !isVisible;
        default:
          return isVisible;
      }
    }

    default:
      return true;
  }
};

/**
 * Check if a section should be displayed based on enableWhen conditions
 */
export const isSectionEnabled = (section, schema, formValues, _visited = new Set()) => {
  if (!section.enableWhen || section.enableWhen.length === 0) return true;

  const behavior = section.enableBehavior || 'any';
  const results = section.enableWhen.map(c => evaluateCondition(c, formValues, schema, _visited));
  return behavior === 'all' ? results.every(Boolean) : results.some(Boolean);
};

/**
 * Check if question should be displayed based on enableWhen conditions.
 * Supports universal sourceType conditions and legacy question-only format.
 */
export const isQuestionEnabled = (question, formValues, schema) => {
  if (!question.enableWhen || question.enableWhen.length === 0) return true;

  const behavior = question.enableBehavior || 'any';
  const results = question.enableWhen.map(c => evaluateCondition(c, formValues, schema));
  return behavior === 'all' ? results.every(Boolean) : results.some(Boolean);
};

/**
 * Check if answer should be displayed based on enableWhen condition
 * (depends on other answers in same question — legacy sibling-based format)
 */
export const isAnswerEnabled = (answer, question, allAnswers, rendering, formValues) => {
  if (answer.enableWhen?.answerIndex !== undefined) {
    const dependentAnswer = allAnswers?.[answer.enableWhen.answerIndex];
    if (!dependentAnswer) return true;
    const dependentAnswerId = dependentAnswer.concept || dependentAnswer.conceptCode || `answer_${answer.enableWhen.answerIndex}`;
    const sourceValue = rendering === 'radio'
      ? formValues[question.id]
      : formValues[question.id]?.[dependentAnswerId];
    // Checkbox stores unchecked as false — must not count as "exists" (reuse evaluateOperator).
    // Radio stores the selected answer id — exists means that sibling is the selection.
    const expected = answer.enableWhen.answerCoding || dependentAnswerId;
    if (rendering === 'radio') {
      const selected = sourceValue === dependentAnswerId;
      switch (answer.enableWhen.operator) {
        case '=': return sourceValue === expected;
        case '!=': return sourceValue !== expected;
        case 'exists': return selected;
        case '!exists': return !selected;
        default: return true;
      }
    }
    return evaluateOperator(answer.enableWhen.operator, sourceValue, expected);
  }
  return true;
};
